import React from 'react';
import MaterialIcon from '@/components/MaterialIcon';
import SmartLink from '@/components/SmartLink';
import { useTracking } from '@mygpt/app/utils';

/**
 * FAQ Section - 百貨店・商業施設向けよくあるご質問
 *
 * 2列のQ&Aカードグリッド
 * 各カードは青いQマークと緑のAマークで構成
 */
export const FAQSection: React.FC = () => {
  const { trackContactClick } = useTracking();

  const faqs = [
    {
      question: '百貨店のフロアマップ連携は可能ですか？',
      answer:
        'はい、既存のフロアマップ画像や3Dマップデータを取り込み、現在地から目的地までのルート案内が可能です。エレベーター・エスカレーター優先など、お客様に合わせたルート設定もできます。',
    },
    {
      question: 'テナント情報の更新は簡単ですか？',
      answer:
        '各テナントのWebサイトやInstagram、PDFカタログを自動でクロールし、最新情報を常に反映します。新店舗オープンや閉店情報も、管理画面から即座に反映可能です。',
    },
    {
      question: 'インバウンド（訪日外国人）対応はできますか？',
      answer:
        '英語、中国語（簡体・繁体）、韓国語、タイ語、ベトナム語など10言語以上に自動対応。免税カウンターの場所、外貨両替、Wi-Fi情報なども多言語で案内できます。',
    },
    {
      question: 'AIの回答精度はどの程度ですか？',
      answer:
        '導入時に施設の情報を学習させることで、高い精度で回答できます。運用中も管理画面から回答内容を確認・修正でき、継続的に精度を向上させることが可能です。',
    },
    {
      question: '専用端末・キオスク筐体は必要ですか？',
      answer:
        '不要です。iPad、既存のサイネージ、またはお客様自身のスマートフォン（LINE/Webブラウザ）で動作します。高額な専用端末への投資なしで導入できます。',
    },
    {
      question: '導入までの期間はどれくらいですか？',
      answer:
        'テナント情報（PDF/Web）を読み込ませるだけで、最短1週間で構築完了。従来のシステムと比較して、導入期間を大幅に短縮できます。14日間の無料トライアルもご用意しています。',
    },
  ];

  return (
    <section className="w-full px-4 md:px-10 lg:px-40 py-20 bg-white">
      <div className="max-w-[1000px] w-full mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-solution-orange font-bold tracking-wider uppercase text-sm mb-2 block">
            よくあるご質問 - 商業施設向けAIチャットボットについて
          </span>
          <h2 className="text-3xl font-black text-solution-text">
            百貨店・商業施設向け
            <br className="md:hidden" />
            よくあるご質問
          </h2>
        </div>

        {/* Q&A Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-8 border border-solution-border shadow-sm flex flex-col gap-4"
            >
              {/* Question */}
              <div className="flex items-start gap-4">
                <div className="shrink-0 size-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-black">
                  Q
                </div>
                <h3 className="font-bold text-lg text-solution-text pt-1">{faq.question}</h3>
              </div>
              {/* Answer */}
              <div className="flex items-start gap-4">
                <div className="shrink-0 size-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-black">
                  A
                </div>
                <p className="text-solution-text-secondary leading-relaxed pt-1">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ末尾CTA */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 bg-white rounded-2xl border border-solution-border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="size-12 rounded-full bg-solution-orange/10 flex items-center justify-center">
                <MaterialIcon name="help_center" className="text-solution-orange text-2xl" />
              </div>
              <p className="text-solution-text font-medium">まだ疑問がありますか？</p>
            </div>
            <SmartLink
              href="/contact"
              className="flex items-center justify-center gap-2 rounded-lg h-12 px-6 bg-solution-orange hover:bg-solution-orange-dark transition-colors text-white text-sm font-bold"
              onClick={() => trackContactClick('faq')}
            >
              <MaterialIcon name="support_agent" className="text-lg" />
              専門スタッフに相談する
            </SmartLink>
          </div>
        </div>

        {/* FAQ後引導 */}
        <div className="mt-8 text-center">
          <SmartLink
            to="#final-cta"
            className="inline-flex items-center gap-2 px-6 py-3 bg-solution-orange/10 hover:bg-solution-orange/20 rounded-full transition-all cursor-pointer group"
          >
            <MaterialIcon name="arrow_downward" className="text-solution-orange group-hover:animate-bounce" />
            <span className="font-bold text-solution-orange">今すぐ始める</span>
            <MaterialIcon name="arrow_downward" className="text-solution-orange group-hover:animate-bounce" />
          </SmartLink>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
